import React, { useState, useEffect } from 'react';
import { ChevronUp, ChevronDown } from '../../shared/components/Icons';
import { confColor } from './confColorUtils';
import TopicRow from './TopicRow';
import StudyUnitRow from './StudyUnitRow';
import StudyUnitGroupHeader from './StudyUnitGroupHeader';

// General Topic group — L2 level.
// Per the canonical design: L2 is a subtle label, not a card and not a
// collapse target. Study units / subtopics always render below it.
function GeneralTopicGroup({ generalTopic, topics, moduleColor, focusTopicId, statuses, onNavigateToSRA, detailMode, studyUnits, onSave, onTopicsBulkUpdate }) {
  const done = topics.filter(t => t.confidence !== null).length;
  const hasAny = done > 0;
  const avg = hasAny ? topics.reduce((s, t) => s + (t.confidence || 0), 0) / topics.length : null;

  // Aggregate pulse class — matches the canonical's L2 pulse dot.
  // Subtle RAG signal at scan. "neutral" when no member has been rated.
  const pulseClass = !hasAny ? 'l2-pulse-neutral'
    : avg == null ? 'l2-pulse-neutral'
    : avg * 100 <= 30 ? 'l2-pulse-red'
    : avg * 100 <= 60 ? 'l2-pulse-amber'
    : 'l2-pulse-green';

  const childLabel = detailMode === 'study_units' && studyUnits
    ? (() => {
        const n = studyUnits.length;
        return `${n} study unit${n === 1 ? '' : 's'}`;
      })()
    : `${topics.length} subtopic${topics.length === 1 ? '' : 's'}`;

  return (
    <div className="general-topic-group">
      <div className="general-topic-header" role="heading" aria-level="3">
        <span className="general-topic-name">{generalTopic}</span>
        <div className="general-topic-right">
          <span className={`l2-pulse ${pulseClass}`} title={hasAny ? `Aggregate: ${avg * 100 <= 30 ? 'weak' : avg * 100 <= 60 ? 'needs work' : 'strong'}` : 'Not rated'} />
          <span className="general-topic-count">{childLabel}</span>
        </div>
      </div>
      <div className="general-topic-topics">
        {detailMode === 'study_units' && studyUnits ? (
          studyUnits.map(su => {
            const memberSet = new Set(su.member_topic_ids);
            const members = topics.filter(t => memberSet.has(t.id));
            return (
              <StudyUnitRow
                key={su.id}
                studyUnit={su}
                members={members}
                onSave={onSave}
                onTopicsBulkUpdate={onTopicsBulkUpdate}
                moduleColor={moduleColor}
                statuses={statuses}
                focusTopicId={focusTopicId}
                onNavigateToSRA={onNavigateToSRA}
              />
            );
          })
        ) : studyUnits ? (
          // Detailed subtopics mode — group L3s by study unit with a
          // compact header + slider above each group.
          studyUnits.map(su => {
            const memberSet = new Set(su.member_topic_ids);
            const members = topics.filter(t => memberSet.has(t.id));
            if (members.length === 0) return null;
            return (
              <React.Fragment key={su.id}>
                <StudyUnitGroupHeader
                  studyUnit={su}
                  members={members}
                  onTopicsBulkUpdate={onTopicsBulkUpdate}
                />
                {members.map(t => (
                  <TopicRow
                    key={t.id}
                    topic={t}
                    onSave={onSave}
                    forceOpen={null}
                    moduleColor={moduleColor}
                    isFocused={t.id === focusTopicId}
                    statuses={statuses}
                    sraStat={null}
                    onNavigateToSRA={onNavigateToSRA}
                  />
                ))}
              </React.Fragment>
            );
          })
        ) : (
          // Fallback if studyUnits haven't loaded yet
          topics.map(t => (
            <TopicRow
              key={t.id}
              topic={t}
              onSave={onSave}
              forceOpen={null}
              moduleColor={moduleColor}
              isFocused={t.id === focusTopicId}
              statuses={statuses}
              sraStat={null}
              onNavigateToSRA={onNavigateToSRA}
            />
          ))
        )}
      </div>
    </div>
  );
}

// Slug a subject-area name for use as a DOM id.
// "Business Law and Practice" → "business-law-and-practice"
export function subjectSlug(name) {
  return String(name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

// Main Topic (subject) group — top level, open by default
function SubjectGroup({ subject, topics, onSave, forceOpen, moduleColor, focusTopicId, statuses, highlightFirstTopic, sraStat, sraTopicStats, onRateAll, autoExpandL2, onNavigateToSRA, detailMode, studyUnits, onTopicsBulkUpdate }) {
  const [open, setOpen] = useState(true);
  const done = topics.filter(t=>t.confidence!==null).length;
  const hasAny = done > 0;
  const avg = hasAny ? topics.reduce((s,t)=>s+(t.confidence||0),0)/topics.length : null;
  const hasFocus = topics.some(t=>t.id===focusTopicId);
  useEffect(()=>{ if(hasFocus) setOpen(true); },[hasFocus]);
  useEffect(()=>{ if(highlightFirstTopic) setOpen(true); },[highlightFirstTopic]);
  useEffect(()=>{ if(forceOpen!==null) setOpen(forceOpen); },[forceOpen]);

  const sraColor = sraStat?.accuracy == null ? null
    : sraStat.accuracy >= 0.7 ? 'var(--green)' : sraStat.accuracy >= 0.5 ? 'var(--amber)' : 'var(--red)';

  // Group topics by general_topic within this subject
  const byGeneral = {};
  const generalOrder = [];
  topics.forEach(t => {
    const key = t.general_topic || 'General';
    if (!byGeneral[key]) { byGeneral[key] = []; generalOrder.push(key); }
    byGeneral[key].push(t);
  });
  // Remove dupes while preserving order
  const uniqueGenerals = [...new Set(generalOrder)];

  // Avg badge class — signal band per canonical design reference
  const avgClass = avg == null ? null
    : avg * 100 <= 30 ? 'avg-weak'
    : avg * 100 <= 60 ? 'avg-moderate'
    : 'avg-strong';

  return (
    <div className="subject-group" id={`subject-${subjectSlug(subject)}`}>
      <div className="subject-header" onClick={()=>setOpen(o=>!o)} role="heading" aria-level="2">
        <span className="subject-name">{subject}</span>
        <div className="subject-right">
          <span className="subject-count">{done} of {topics.length} rated</span>
          {avg !== null && (
            <span className={`subject-avg-badge ${avgClass}`}>
              avg {Math.round(avg*100)}%
            </span>
          )}
          {sraStat?.accuracy != null && (
            <span className="subject-sra-badge" style={{color:sraColor}} title={`SRA questions: ${sraStat.attempted} of ${sraStat.total} attempted, ${Math.round(sraStat.accuracy*100)}% correct`}>
              SRA {Math.round(sraStat.accuracy*100)}%
            </span>
          )}
          <span className="chevron">{open?<ChevronUp size={10} />:<ChevronDown size={10} />}</span>
        </div>
      </div>
      {open&&(
        <div className="subject-topics">
          {uniqueGenerals.map((genTopic, gi) => (
            <GeneralTopicGroup
              key={genTopic}
              generalTopic={genTopic}
              topics={byGeneral[genTopic]}
              onSave={onSave}
              forceOpen={forceOpen}
              moduleColor={moduleColor}
              focusTopicId={focusTopicId}
              statuses={statuses}
              onRateAll={onRateAll}
              autoOpen={autoExpandL2}
              onNavigateToSRA={onNavigateToSRA}
              detailMode={detailMode}
              studyUnits={studyUnits ? studyUnits.filter(su => su.general_topic === genTopic) : null}
              onTopicsBulkUpdate={onTopicsBulkUpdate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export { GeneralTopicGroup };
export default SubjectGroup;
